import React, { useState, useEffect } from 'react';
import { StorageService } from '../services/storage';
import { APPS_DATA } from '../data/mockData';
import { AppCollection } from '../types';
import { StoreBadge } from '../components/StoreBadge';
import { AppIcon } from '../components/AppIcon';
import { FolderKanban, Plus, Scale, X } from 'lucide-react';

interface CollectionsViewProps {
  onSelectApp: (appId: string) => void;
  onCompare: (appIds: string[]) => void;
}

export const CollectionsView: React.FC<CollectionsViewProps> = ({
  onSelectApp,
  onCompare
}) => {
  const [collections, setCollections] = useState<AppCollection[]>(StorageService.getCollections());
  const [selectedColId, setSelectedColId] = useState<string>(collections[0]?.id || '');
  const [newColName, setNewColName] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const refresh = () => setCollections(StorageService.getCollections());
    window.addEventListener('appstatsview_collections_updated', refresh);
    return () => window.removeEventListener('appstatsview_collections_updated', refresh);
  }, []);

  const activeCollection = collections.find((c) => c.id === selectedColId) || collections[0];

  const appsInActive = activeCollection
    ? activeCollection.appIds.map((id) => APPS_DATA[id]).filter(Boolean)
    : [];

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newColName.trim()) return;
    const created = StorageService.createCollection(newColName.trim(), 'Custom user app collection', []);
    setCollections(StorageService.getCollections());
    setSelectedColId(created.id);
    setNewColName('');
    setShowCreate(false);
  };

  const handleRemoveFromCol = (appId: string) => {
    if (activeCollection) {
      StorageService.removeAppFromCollection(activeCollection.id, appId);
      setCollections(StorageService.getCollections());
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
            <FolderKanban className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Curated App Lists</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            App Collections
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Organize applications into custom intelligence decks, client benchmarks, and competitive cohorts.
          </p>
        </div>

        <button
          onClick={() => setShowCreate(!showCreate)}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2.5 rounded-2xl shadow-md transition-colors self-start md:self-auto"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          <span>New Collection</span>
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-3 max-w-lg animate-fadeIn">
          <h4 className="font-bold text-sm text-slate-900">Create New Collection</h4>
          <input
            type="text"
            placeholder="Collection Name (e.g. Fintech Disruptors)..."
            value={newColName}
            onChange={(e) => setNewColName(e.target.value)}
            className="w-full text-xs p-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="text-xs text-slate-500 px-3 py-1.5 rounded-xl hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-xs bg-blue-600 text-white font-bold px-4 py-1.5 rounded-xl"
            >
              Create Collection
            </button>
          </div>
        </form>
      )}

      {/* Main Grid: Collections sidebar + apps */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left: Collections list */}
        <div className="lg:col-span-4 space-y-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block px-1">
            Your Collections ({collections.length})
          </span>

          {collections.map((col) => {
            const isSelected = activeCollection?.id === col.id;
            return (
              <div
                key={col.id}
                onClick={() => setSelectedColId(col.id)}
                className={`p-4 rounded-3xl border cursor-pointer transition-all ${
                  isSelected
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-500/20'
                    : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200/80 shadow-2xs'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: isSelected ? '#ffffff' : col.color }}
                    />
                    <h3 className="font-bold text-sm tracking-tight">{col.name}</h3>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {col.appIds.length} apps
                  </span>
                </div>
                <p className={`text-xs mt-2 line-clamp-2 ${isSelected ? 'text-blue-100' : 'text-slate-500'}`}>
                  {col.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Right: Apps in active collection */}
        <div className="lg:col-span-8 space-y-4">
          {activeCollection && (
            <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
                <div>
                  <h3 className="text-xl font-black text-slate-900 tracking-tight">
                    {activeCollection.name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">{activeCollection.description}</p>
                </div>

                {appsInActive.length > 1 && (
                  <button
                    onClick={() => onCompare(appsInActive.map((a) => a.id).slice(0, 5))}
                    className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-xs px-3.5 py-2 rounded-xl border border-blue-200 transition-colors"
                  >
                    <Scale className="w-3.5 h-3.5" strokeWidth={1.75} />
                    <span>Compare Collection Apps</span>
                  </button>
                )}
              </div>

              {appsInActive.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-xs">
                  No apps added to this collection yet. Open any app page and tap &ldquo;Add to Collection&rdquo;.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {appsInActive.map((app) => (
                    <div
                      key={app.id}
                      className="py-3.5 flex items-center justify-between gap-3 group"
                    >
                      <div
                        onClick={() => onSelectApp(app.id)}
                        className="flex items-center gap-3 cursor-pointer min-w-0"
                      >
                        <AppIcon
                          app={app}
                          size="sm"
                          className="w-10 h-10 rounded-xl shadow-2xs flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                              {app.name}
                            </span>
                            <StoreBadge store={app.primaryStore} />
                          </div>
                          <span className="text-xs text-slate-400 block">{app.developer} • Rank #{app.currentRank}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => onSelectApp(app.id)}
                          className="text-xs font-bold text-blue-600 hover:underline"
                        >
                          View Stats
                        </button>
                        <button
                          onClick={() => handleRemoveFromCol(app.id)}
                          className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50 transition-colors"
                          title="Remove from collection"
                        >
                          <X className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
